<script>
import Fixtures from "@/services/Fixtures";
import Swal from "sweetalert2";

export default {
  props: {
    matchDetails: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      match_status: "",
      datetime: "",
      score_home: 0,
      score_away: 0,
      round: "",
      matchStatuses: [],
      tryingToSubmit: false,
    };
  },

  watch: {
    matchDetails: {
      immediate: true,
      handler(item) {
        if (!item) return;
        this.match_status = item.match_status || "";
        this.score_home = item.score_home != null ? item.score_home : 0;
        this.score_away = item.score_away != null ? item.score_away : 0;
        this.round = item.round != null ? item.round : "";
        this.datetime = this.toDatetimeLocal(item.date_time);
      },
    },
  },

  mounted() {
    this.loadMatchStatuses();
  },

  methods: {
    closeModal() {
      this.$bvModal.hide("edit_sport_data_match");
    },

    toDatetimeLocal(value) {
      if (!value) return "";
      const date = new Date(value.replace(" ", "T"));
      if (Number.isNaN(date.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },

    formatDatetime(value) {
      if (!value) return "";
      const normalized = value.replace("T", " ");
      return normalized.length === 16 ? `${normalized}:00` : normalized;
    },

    formatError(error) {
      if (!error) return "Failed to update match";
      if (Array.isArray(error)) return error.join(", ");
      return error;
    },

    async loadMatchStatuses() {
      try {
        const response = await Fixtures.getSportMatchStatuses();
        this.matchStatuses = response.data.match_status || response.data.data?.match_status || [];
      } catch (error) {
        console.error("Error loading match statuses:", error);
      }
    },

    async updateSportMatch() {
      if (!this.matchDetails || !this.matchDetails.match_id) return;

      this.tryingToSubmit = true;
      const payload = {
        score_home: Number(this.score_home),
        score_away: Number(this.score_away),
      };

      if (this.match_status) payload.match_status = this.match_status;
      if (this.datetime) payload.date_time = this.formatDatetime(this.datetime);
      if (this.round !== "") payload.round = this.round;

      try {
        await Fixtures.updateSportMatch(this.matchDetails.match_id, payload);
        Swal.fire("Success!", "Match updated successfully!", "success");
        this.closeModal();
        this.$emit("onRefresh");
      } catch (error) {
        const err = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : "";
        Swal.fire("Fail!", this.formatError(err), "warning");
      } finally {
        this.tryingToSubmit = false;
      }
    },
  },
};
</script>

<template>
  <b-modal
    id="edit_sport_data_match"
    title="Edit Match"
    title-class="font-18"
    centered
    size="lg"
  >
    <form @submit.prevent="updateSportMatch">
      <div class="form-group mb-3">
        <label>Match ID</label>
        <input
          type="text"
          class="form-control"
          :value="matchDetails ? matchDetails.match_id : ''"
          disabled
        />
      </div>

      <div class="row">
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="edit_match_status">Status</label>
            <b-form-select id="edit_match_status" v-model="match_status">
              <option value="">Select status</option>
              <option
                v-for="status in matchStatuses"
                :key="status.match_status_id || status.match_status"
                :value="status.match_status"
              >
                {{ status.match_status }}
              </option>
            </b-form-select>
          </div>
        </div>
        <div class="col-md-6">
          <div class="form-group mb-3">
            <label for="edit_match_datetime">Date & Time</label>
            <input
              id="edit_match_datetime"
              v-model="datetime"
              type="datetime-local"
              class="form-control"
            />
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-4">
          <div class="form-group mb-3">
            <label for="edit_score_home">Home Score</label>
            <input
              id="edit_score_home"
              v-model.number="score_home"
              type="number"
              min="0"
              class="form-control"
            />
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group mb-3">
            <label for="edit_score_away">Away Score</label>
            <input
              id="edit_score_away"
              v-model.number="score_away"
              type="number"
              min="0"
              class="form-control"
            />
          </div>
        </div>
        <div class="col-md-4">
          <div class="form-group mb-3">
            <label for="edit_match_round">Round</label>
            <input id="edit_match_round" v-model="round" type="number" min="0" class="form-control" />
          </div>
        </div>
      </div>
    </form>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="updateSportMatch" :disabled="tryingToSubmit">
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Save
      </b-button>
    </template>
  </b-modal>
</template>
